/**
 * AuthedMedia
 *
 * Chat media (images/video/audio/documents) is served from the protected
 * `/api/media/:filename` endpoint, which now requires a bearer token (Chunk A lockdown).
 * Plain `<img src>` / `<video src>` can't send Authorization headers, so we fetch the file
 * with authFetch, wrap it in an in-memory blob object URL, and render that. The object URL
 * is revoked on unmount / filename change to avoid leaks.
 *
 * Keeps sensitive customer media (e.g. immigration documents) private — anonymous requests
 * to /api/media still get 401.
 */
import { useEffect, useState } from 'react';
import { authFetch } from '../api/orchestrator';

function useAuthedMediaUrl(filename: string | null | undefined): {
  url: string | null;
  error: boolean;
  loading: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!filename) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(false);

    (async () => {
      try {
        const res = await authFetch(`/api/media/${filename}`);
        if (!res.ok) throw new Error(`media ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename]);

  return { url, error, loading };
}

/** Download a protected media file via an authenticated fetch. Returns false if unavailable. */
export async function downloadAuthedMedia(filename: string): Promise<boolean> {
  try {
    const res = await authFetch(`/api/media/${filename}`);
    if (!res.ok) return false;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoke after a delay so the download has time to start.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    return true;
  } catch {
    return false;
  }
}

interface AuthedMediaProps {
  filename: string;
  mediaType: string;
  fileName?: string | null;
}

export function AuthedMedia({ filename, mediaType, fileName }: AuthedMediaProps) {
  const { url, error, loading } = useAuthedMediaUrl(filename);

  if (error) {
    return (
      <div className="message-media-error" style={{ fontSize: 12, color: '#999' }}>
        Media unavailable
      </div>
    );
  }
  if (loading || !url) {
    return (
      <div className="message-media-loading" style={{ fontSize: 12, color: '#999' }}>
        Loading media…
      </div>
    );
  }

  if (mediaType.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={fileName || 'Image'}
        className="message-image"
        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', cursor: 'pointer' }}
        onClick={() => window.open(url, '_blank')}
      />
    );
  }
  if (mediaType.startsWith('video/')) {
    return (
      <video
        src={url}
        controls
        className="message-video"
        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
      >
        Your browser doesn't support video playback.
      </video>
    );
  }
  if (mediaType.startsWith('audio/')) {
    return (
      <div className="message-audio">
        <audio src={url} controls className="message-audio-player" />
        {fileName && <div className="audio-filename">{fileName}</div>}
      </div>
    );
  }
  return null;
}
